import React from 'react';

// controls
import { JxTooltip, JxTooltipRows, JxTooltipRowItemDescription } from '@jx';

// locals
import { handleLabelMouseDown } from './UmlConnectorLayer.handleLabelMouseDown.jsx';
import { ConnectorBuilder } from '@jxUml';

/**
 * Renders a single connector: hit-area (with tooltip), visible line, and optional label.
 * Reports hover in/out via hoveredConnectorIdRef so the parent's D-key delete listener knows the target.
 * @param {object} props
 * @param {object} props.connector - connector data
 * @param {Array<{x:number,y:number}>} props.points - resolved path points
 * @param {{x:number,y:number}} props.midpoint - line midpoint (label anchor)
 * @param {object} props.style - resolved visual style
 * @param {Function} props.onConnectorDoubleClick - fn(connector, event, points) — add waypoint
 * @param {Function} props.onLabelMoved - fn(labelId, x, y) — label drag
 * @param {React.RefObject} props.hoveredConnectorIdRef - shared hover-tracking ref
 */
function UmlConnectorPath({ connector, points, midpoint, style, onConnectorDoubleClick, onLabelMoved, onDerivedLabelMoved, hoveredConnectorIdRef }) {
    const markerId = style.arrowHead === 'triangle' ? 'uml-arrow-triangle' : 'uml-arrow-open';
    return (
        <g className="uml-connector-group" data-connector-id={connector.id}>
            {/* Wide transparent hit-area behind the visible line for easier clicking.
                Double-click adds a waypoint; D key while hovered deletes the connector. */}
            <JxTooltip
                followCursor
                title={
                    <JxTooltipRows>
                        <JxTooltipRowItemDescription label="Double click" value="New waypoint" />
                        <JxTooltipRowItemDescription label="Press D" value="Delete connector" />
                    </JxTooltipRows>
                }
            >
                <path
                    className="uml-connector-hit-area"
                    d={ConnectorBuilder.svgPath(points)}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={10}
                    onMouseDown={function selectConnector() {
                        connector.select();
                    }}
                    onDoubleClick={function handleAddWaypoint(e) {
                        onConnectorDoubleClick?.(connector, e, points);
                    }}
                    onMouseEnter={function markHovered() {
                        hoveredConnectorIdRef.current = connector.id;
                    }}
                    onMouseLeave={function clearHovered() {
                        if (hoveredConnectorIdRef.current === connector.id) {
                            hoveredConnectorIdRef.current = null;
                        }
                    }}
                />
            </JxTooltip>
            <path
                className="uml-connector-visible-path"
                d={ConnectorBuilder.svgPath(points)}
                fill="none"
                stroke={style.color}
                strokeWidth={style.strokeWidth}
                strokeDasharray={style.dash ? '4 3' : undefined}
                markerEnd={`url(#${markerId})`}
            />
            {connector.derivedLabel ? (
                <text
                    className="uml-connector-label uml-connector-label--derived"
                    x={midpoint.x + (connector.derivedLabelOffset?.x || 0)}
                    y={midpoint.y + (connector.derivedLabelOffset?.y || 0)}
                    onMouseDown={function startDerivedLabelDrag(e) {
                        connector.select();
                        // Synthetic label object — derived connectors have no real UmlCaption,
                        // so we hand handleLabelMouseDown a plain {id, x, y} keyed on the REAL
                        // connector id (see InterfaceReductionResolver.js derivedLabelOffset).
                        const syntheticLabel = { id: connector.id, x: connector.derivedLabelOffset?.x || 0, y: connector.derivedLabelOffset?.y || 0 };
                        handleLabelMouseDown(syntheticLabel, midpoint, onDerivedLabelMoved, e);
                    }}
                >
                    {connector.derivedLabel}
                </text>
            ) : (
                connector.label && (
                    <text
                        className="uml-connector-label"
                        x={midpoint.x + connector.label.x}
                        y={midpoint.y + connector.label.y}
                        onMouseDown={function startLabelDrag(e) {
                            connector.select();
                            handleLabelMouseDown(connector.label, midpoint, onLabelMoved, e);
                        }}
                    >
                        {connector.label.text}
                    </text>
                )
            )}
        </g>
    );
}

export default UmlConnectorPath;
